import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {getAuth} from '../modules/auth/core/AuthHelpers'

const AppApi = createApi({
  reducerPath: 'AppApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    prepareHeaders: (headers) => {
      const auth = getAuth()
      if (auth?.isAuthenticated) {
        headers.set('Authorization', `Bearer ${auth.token}`)
        headers.set('Access-Control-Allow-Origin', '*')
      }
      return headers
    },
  }),
  tagTypes: ['precall-policy', 'AgentStatus'],
  endpoints: (builder) => ({
    acceptCall: builder.mutation<any, {contactId: string; action: boolean}>({
      query: (body) => ({
        url: '/aws-connect/agents/accept-call',
        method: 'POST',
        body,
      }),
    }),
    markNotificationReceived: builder.mutation<any, {callHeaderId: number | string}>({
      query: (body) => ({
        url: '/aws-connect/agents/mark-notification-received',
        method: 'POST',
        body,
      }),
    }),
    setCallEndedByInterpreter: builder.mutation<any, {code: number | string}>({
      query: (body) => ({
        url: '/aws-connect/v4/call-header/set-ended-by-interpreter',
        method: 'POST',
        body,
      }),
    }),
    // Future fallback: GET /aws-connect/agents/call-outcome.
    // Primary path is InterpreterHub SignalR `call:outcome` (same State / RetryAfterMs shape).
    // Keep this endpoint wired so a poll-based fallback can be re-enabled if SignalR is missed
    // (connection down) without reinventing the contract.
    getCallOutcome: builder.query<
      {
        state: 'PENDING' | 'WON' | 'TAKEN' | 'REROUTED' | 'ROUTED' | 'ENDED' | 'UNKNOWN'
        retryAfterMs: number
      },
      {contactId: string}
    >({
      query: ({contactId}) => ({
        url: `/aws-connect/agents/call-outcome?contactId=${encodeURIComponent(contactId)}`,
        method: 'GET',
      }),
    }),
    /** Destination E.164 for outbound softphone recovery probe (agent.connect). */
    getSoftphoneProbeConfig: builder.query<
      {destinationPhoneNumber: string; timeoutMs: number},
      void
    >({
      query: () => ({
        url: '/aws-connect/agents/softphone-probe-config',
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useAcceptCallMutation,
  useMarkNotificationReceivedMutation,
  useSetCallEndedByInterpreterMutation,
  // Intentionally unused by the live standby path today — reserved for a future
  // HTTP poll fallback when SignalR call:outcome cannot be relied on.
  useLazyGetCallOutcomeQuery,
  useLazyGetSoftphoneProbeConfigQuery,
} = AppApi

export default AppApi
